import * as Sentry from "@sentry/react";
import classNames from "classnames";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import InlineSVG from "react-inlinesvg";
import Modal from "react-modal";
import { MAX_WORLDBUILDING_IMPORT_INPUT_WORDS } from "../../../../../src/shared/constants";
import {
  DEFAULT_ROLES,
  MAX_NUM_CHARACTERS_PER_FIELD,
  MAX_NUM_CHARACTERS_PER_FIELD_LABEL,
  MAX_NUM_CUSTOM_FIELDS,
  MAX_NUM_ENTRIES_PER_PROJECT,
} from "../../../../../src/shared/schemas/WorldbuildingEntrySchema";
import Auth from "../../../Auth";
import useKeyboardShortcuts from "../../../hooks/useKeyboardShortcuts";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import { useAppStore } from "../../../stores/appStore";
import Utils, { countWords } from "../../../Utils";
import { FileImportButton } from "../../FileImportButton";
import IconButton from "../../IconButton";
import InlineWarning from "../../InlineWarning";
import { LoadingButton } from "../../StoryBible/StyleMatcher/LoadingButton";
import { Tag } from "../../Tag";
import TextButton from "../../TextButton";
import { useToast } from "../../ToastWrapper";
import { ImportWorldbuildingContainer, ModalInnerContainer } from "./styles";

const MAX_UNSTRUCTURED_ENTITIES = 30;
const MAX_LENGTH_PARSED_FIELDS_DISPLAY = 100;

const IMPORT_ERROR_MESSAGE_MAP = {
  MAX_WORDS: `Sudowrite can only import ${Utils.numberWithCommas(
    MAX_WORLDBUILDING_IMPORT_INPUT_WORDS
  )} words from most documents. Please upload a smaller document, or use our handy template to upload a larger amount of data at once.`,
  MAX_FIELD_LABEL_LENGTH: `Sudowrite can only import fields with labels up to ${MAX_NUM_CHARACTERS_PER_FIELD_LABEL} characters. Please ensure your column headings are less than ${MAX_NUM_CHARACTERS_PER_FIELD_LABEL} characters, and try again.`,
  MAX_FIELD_VALUE_LENGTH: `Sudowrite can only import fields with values of up to ${Utils.numberWithCommas(
    MAX_NUM_CHARACTERS_PER_FIELD
  )} characters. Please ensure your cells contain less than ${Utils.numberWithCommas(MAX_NUM_CHARACTERS_PER_FIELD)} characters, and try again.`,
  MAX_CUSTOM_FIELDS: `Sudowrite can only import worldbuilding entries with up to ${Utils.numberWithCommas(
    MAX_NUM_CUSTOM_FIELDS
  )} custom fields. Please ensure your table has less than ${Utils.numberWithCommas(MAX_NUM_CUSTOM_FIELDS)} columns and try again.`,
  UNKNOWN_DATA: "Unable to retrieve the provided file. Please try again.",
};

const modalOuterStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    zIndex: "30000",
    width: "80vw",
    height: "80vh",
    maxWidth: "836px",
    maxHeight: "510px",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.10), 0px 0px 2px 0px rgba(0, 0, 0, 0.25)",
    padding: "18px",
    overflow: "hidden",
    backgroundColor: "var(--menu-color)",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
};

export default function ImportWorldbuilding({
  isOpen, //
  setIsOpen,
  projectId,
  worldbuildingEntries,
  updateEntries,
  endOfListRef,
  toggleIsCustomizingDefaultFields,
  setIsCustomizingWhileImporting,
}) {
  const modalRef = useRef();
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [detectedElements, setDetectedElements] = useState([]); // names of the worldbuilding entries [{ name: string, type: string }]
  const [validating, setValidating] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [importSessionId, setImportSessionId] = useState(null);
  const [fields, setFields] = useState([]); // custom fields
  const [disableFileImport, setDisableFileImport] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [maxWorldbuildingEntries, setMaxWorldbuildingEntries] = useState(MAX_UNSTRUCTURED_ENTITIES);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const isMac = useAppStore((state) => state.isMac);
  const { setToast } = useToast();

  const handleChange = (e) => {
    e.preventDefault();
    const text = e.target.value;
    const wordCount = countWords(text);

    if (wordCount > MAX_WORLDBUILDING_IMPORT_INPUT_WORDS) {
      setToast(`Cannot import more than ${MAX_WORLDBUILDING_IMPORT_INPUT_WORDS} words`);
    } else {
      setImportText(text);
    }
  };

  // resets stat to start over import process
  const reset = () => {
    setImportText("");
    setImportSessionId(null);
    setImporting(false);
    setDisableFileImport(false);
    setDetectedElements([]);
    setFields([]);
    setValidating(false);
    setShowValidation(false);
    setUploadingFile(false);
    setUploadName("");
    setValidationError("");
    setMaxWorldbuildingEntries(MAX_UNSTRUCTURED_ENTITIES);
  };

  const onClose = () => {
    reset();
    setIsOpen(false);
  };

  const getRelevantRolesText = () => {
    let formatted = "";
    const validRoles = DEFAULT_ROLES.map((role) => role.name);
    const roles = detectedElements
      .filter((element) => element && element.type)
      .map((name) => _.startCase(name.type.trim()))
      .filter((role) => role !== "" && validRoles.includes(role));

    const uniqueRoles = [...new Set(roles)];

    if (uniqueRoles.length === 0) {
      return null;
    } else if (uniqueRoles.length === 1) {
      return `The type that seems most relevant to your import is ${uniqueRoles[0]}.`;
    }

    formatted += "Types that seem most relevant to your import are: ";

    uniqueRoles.forEach((role, index) => {
      if (index === uniqueRoles.length - 2) {
        formatted += `${role}, and`;
      } else if (index === uniqueRoles.length - 1) {
        formatted += ` ${role}.`;
      } else {
        formatted += `${role}, `;
      }
    });

    return formatted;
  };

  const handleImport = async () => {
    try {
      setImporting(true);
      const displayOrder = Math.max(...worldbuildingEntries.map((c) => c.displayOrder), 0) + 1;

      const params = {
        projectId,
        displayOrder,
        elements: detectedElements.length > 0 ? detectedElements : undefined, // we only want to send elements if they were detected
      };

      if (importSessionId) {
        params.sessionId = importSessionId;
      } else {
        params.text = importText;
      }

      const response = await Auth.fetchWithIdToken("/api/ai/storyEngine/importWorldbuilding", params);

      updateEntries([...worldbuildingEntries, ...response.worldbuildingEntries]);

      // Defer until the new entries commit and the end-of-list sentinel moves
      // below them (same deferral as the generateEntry scroll path).
      setTimeout(() => {
        endOfListRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 200);
      onClose();
    } catch (err) {
      if (err?.response?.status === 400) {
        const { error: errorCode } = await err.response.json();
        const message = IMPORT_ERROR_MESSAGE_MAP[errorCode] || "No worldbuilding elements were found in the text.";
        setToast(message);
      } else {
        Sentry.captureException(err, { tags: { projectId } });
        setToast("Failed to import worldbuilding elements");
      }
    } finally {
      setImporting(false);
    }
  };

  const handleValidate = async () => {
    try {
      setValidating(true);

      const params = { projectId };

      // only send text if we don't have import data stored in Redis
      // importSessionId is set to retrieve the data from Redis when it is cached
      if (importSessionId) {
        params.sessionId = importSessionId;
      } else {
        params.text = importText;
      }

      const response = await Auth.fetchWithIdToken("/api/ai/storyEngine/detectWorldbuilding", params);
      setDetectedElements(response.names);

      if (response.fields) {
        setFields(response.fields);
      }
      setValidationError("");
      setValidating(false);
      setShowValidation(true);
    } catch (err) {
      const { error } = await err.response.json();

      const validationError = IMPORT_ERROR_MESSAGE_MAP[error];

      if (validationError) {
        setValidationError(validationError);
      } else {
        Sentry.captureException(err, { tags: { projectId } });
        setToast("Failed to import worldbuilding elements");
      }
      setValidating(false);
      setShowValidation(true);
    }
  };

  const formattedParsedFields = (fields) => {
    const formatted = fields.join(", ");

    if (formatted.length > MAX_LENGTH_PARSED_FIELDS_DISPLAY) {
      return formatted.substring(0, MAX_LENGTH_PARSED_FIELDS_DISPLAY) + "...";
    } else {
      return formatted + ".";
    }
  };

  const handleOpenCustomizer = (e) => {
    e.preventDefault();
    setIsOpen(false);
    toggleIsCustomizingDefaultFields(true);
    setIsCustomizingWhileImporting(true);
  };

  const maxWorldbuildingEntriesReached = detectedElements.length > maxWorldbuildingEntries;
  const disableButton = importing || (!importText && !importSessionId) || validating || (showValidation && detectedElements.length === 0) || maxWorldbuildingEntriesReached;
  const showParsedFields = fields.length > 0;

  useEffect(() => {
    // if we have an importSessionId, we should validate the worldbuilding entries
    if (importSessionId) {
      handleValidate();
    }
  }, [importSessionId]);

  useEffect(() => {
    // disable file import if we have text in the textarea
    // otherwise the backend won't know which to use
    if (importText && !disableFileImport) {
      setDisableFileImport(true);
    }

    if (!importText && disableFileImport) {
      setDisableFileImport(false);
    }
  }, [importText, disableFileImport]);

  useEffect(() => {
    // if we have fields, we have structured data, and increase the max worldbuilding entries
    if (fields.length > 0) {
      setMaxWorldbuildingEntries(MAX_NUM_ENTRIES_PER_PROJECT - worldbuildingEntries.length);
    }
  }, [fields.length, worldbuildingEntries.length]);

  // keyboard shortcuts
  const actions = {
    ctrlEnter: (e) => {
      if (isOpen && showValidation && !disableButton) {
        handleImport();
      }
    },
  };

  useKeyboardShortcuts({ actions });

  useOnClickOutside(modalRef, () => {
    if (isOpen) {
      onClose();
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      style={modalOuterStyles}
    >
      <ModalInnerContainer ref={modalRef}>
        <div className="header">
          <div className="header__title">
            <div className="main-title">Import your Worldbuilding Elements</div>
            <div className="sub-title">You can import from anything, even a manuscript! Sudowrite will figure it out.</div>
          </div>
          <div className="close">
            <IconButton
              src="/assets/close.svg"
              onClick={onClose}
            />
          </div>
        </div>
        <ImportWorldbuildingContainer>
          {!showValidation && (
            <div className="inputs">
              <FileImportButton
                setImportSessionId={setImportSessionId}
                handleValidate={handleValidate}
                disable={disableFileImport}
                reset={reset}
                setUploading={setUploadingFile}
                uploadName={uploadName}
                setUploadName={setUploadName}
              />
              <textarea
                className={classNames("paste-text", { disabled: uploadingFile })}
                value={importText}
                onChange={handleChange}
                placeholder="Paste here..."
              />
            </div>
          )}
          {showValidation && !validationError && (
            <div className="validation">
              {uploadName && (
                <div className="validation-section">
                  <p className="validation-heading">Uploaded File:</p>
                  <Tag
                    src={uploadName.endsWith(".csv") ? "/assets/spreadsheet.svg" : "/assets/document.svg"}
                    label={uploadName}
                  />
                </div>
              )}
              {detectedElements.length > 0 ? (
                <div className="validation-section">
                  <p className="validation-heading">Detected Worldbuilding Elements:</p>
                  <ul className="detected-worldbuilding-elements-list">
                    {detectedElements.map((element, index) => (
                      <li
                        key={index}
                        className="detected-worldbuilding-element"
                      >
                        <Tag
                          src="/assets/story-bible/worldbuilding-icon.svg"
                          onDelete={() => setDetectedElements(detectedElements.filter((e) => e.name !== element.name))}
                          label={`${element.name}`}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p>No worldbuilding elements detected.</p>
              )}
            </div>
          )}
          {validationError && (
            <div className="error">
              <InlineWarning text={validationError} />
              <TextButton onClick={reset}>Reset</TextButton>
            </div>
          )}
        </ImportWorldbuildingContainer>
        <div className="footer">
          {!showValidation && (
            <a
              href="/assets/sudowrite-worldbuilding-import-template.csv"
              download
              aria-label="Download worldbuilding import template"
              className="text-button-link"
            >
              <InlineSVG src="/assets/spreadsheet.svg" />
              <p>Download Template</p>
            </a>
          )}
          {showValidation && (
            <div className="info-container">
              {!maxWorldbuildingEntriesReached && !showParsedFields && (
                <p>
                  You can customize the default traits for different types of worldbuilding elements. {getRelevantRolesText()}
                  <button
                    className="link"
                    onClick={handleOpenCustomizer}
                    aria-label="Customize worldbuilding element traits"
                  >
                    Customize Traits
                  </button>
                </p>
              )}
              {maxWorldbuildingEntriesReached && (
                <>
                  <p>{`Please remove ${detectedElements.length - maxWorldbuildingEntries} worldbuilding elements to continue.`}</p>
                  <p>{`We can only import up to ${maxWorldbuildingEntries} worldbuilding elements at a time.`}</p>
                </>
              )}
              {!maxWorldbuildingEntriesReached && showParsedFields && <p>{`Worldbuilding elements will have ${formattedParsedFields(fields)}`}</p>}
            </div>
          )}
          <div className="button-container">
            {!importing && !validating && (
              <button
                onClick={showValidation ? handleImport : handleValidate}
                className={disableButton ? "disabled" : ""}
                aria-label="Import worldbuilding elements"
              >
                {showValidation ? (
                  <>
                    <p>Go</p>
                    <div
                      className="keyboard-shortcut-container"
                      aria-label={`Press ${isMac ? "command" : "control"} plus enter to complete import`}
                    >
                      <InlineSVG
                        src={isMac ? "/assets/keyboard-shortcuts/command-key.svg" : "/assets/keyboard-shortcuts/control-key-pc.svg"}
                        className="keyboard-shortcut"
                        aria-hidden="true"
                      />
                      <InlineSVG
                        src="/assets/keyboard-shortcuts/enter-key.svg"
                        className="keyboard-shortcut"
                        aria-hidden="true"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <InlineSVG src="/assets/import.svg" />
                    <p>Import</p>
                  </>
                )}
              </button>
            )}
            {(importing || validating) && (
              <LoadingButton className="primary">
                <InlineSVG src="/assets/import.svg" />
                {importing ? "Importing" : "Validating"}
              </LoadingButton>
            )}
          </div>
        </div>
      </ModalInnerContainer>
    </Modal>
  );
}
